/* ─────────────────────────────────────────────────────────────────────────────
   GrowthOS migration — Brands master data (day-one slice) — RTK Query slice.

   Transport: the shared axios helpers (get/post/putGeneric/deleteGeneric) from
   common/constants.js, same pattern as d2cApiSlice.js — no separate host or
   custom baseQuery needed. Responses are raw JSON (no envelope); no unwrap().
   ──────────────────────────────────────────────────────────────────────────── */
import { createApi } from '@reduxjs/toolkit/query/react'
import { apiConstants, get, post, putGeneric, patchGeneric, deleteGeneric } from '../common/constants'

const BRANDS = apiConstants.GROWTHOS_ADMIN_BRANDS // 'growthos-admin/brands'
const PEOPLE = apiConstants.GROWTHOS_ADMIN_PEOPLE // 'growthos-admin/people'
const DEALS = apiConstants.GROWTHOS_ADMIN_DEALS // 'growthos-admin/deals'

/* axios call -> RTK Query {data} | {error}. Returns the raw response body (res.data). */
const run = async (promise) => {
  try {
    const res = await promise
    return { data: res?.data }
  } catch (err) {
    const r = err?.response
    return {
      error: {
        status: r?.status,
        data: r?.data, // ProshipException shape: { code, subCode, message, trace }
        message: r?.data?.message || err.message,
      },
    }
  }
}

export const growthosBrandsApiSlice = createApi({
  reducerPath: 'growthosBrandsApi',
  baseQuery: () => ({ data: undefined }), // unused — every endpoint defines its own queryFn
  tagTypes: ['Brand', 'Person'],
  endpoints: (b) => ({
    // Full-parity list — mirrors the real app's single unfiltered /settings/brands response;
    // client derives "Mapping issues" vs "Mapped to HubSpot" itself. No more pagination params.
    listBrands: b.query({
      queryFn: () => run(get(BRANDS)),
      providesTags: ['Brand'],
    }),
    getBrand: b.query({
      queryFn: (id) => run(get(`${BRANDS}/${id}`)),
      providesTags: ['Brand'],
    }),
    createBrand: b.mutation({
      queryFn: (body) => run(post(BRANDS, body)),
      invalidatesTags: ['Brand'],
    }),
    updateBrand: b.mutation({
      queryFn: ({ id, body }) => run(putGeneric(`${BRANDS}/${id}`, body)),
      invalidatesTags: ['Brand'],
    }),
    archiveBrand: b.mutation({
      queryFn: (id) => run(post(`${BRANDS}/${id}/archive`)),
      invalidatesTags: ['Brand'],
    }),
    linkDeal: b.mutation({
      queryFn: ({ id, hubspotDealId, source }) => run(post(`${BRANDS}/${id}/link-deal`, { hubspotDealId, source })),
      invalidatesTags: ['Brand'],
    }),
    unlinkDeal: b.mutation({
      queryFn: ({ id, dealId }) => run(deleteGeneric(`${BRANDS}/${id}/deal-links/${dealId}`)),
      invalidatesTags: ['Brand'],
    }),
    // Correct (or add) one mapping's effective HubSpot Deal ID — PUT /{id}/deal-mappings/{sourceDealId}.
    correctMapping: b.mutation({
      queryFn: ({ id, sourceDealId, hubspotDealId }) =>
        run(putGeneric(`${BRANDS}/${id}/deal-mappings/${sourceDealId}`, { hubspotDealId })),
      invalidatesTags: ['Brand'],
    }),
    // Inline segment/Max SOW/Sales POC/Excluded/Hidden edits — present-key-only PATCH semantics.
    patchBrand: b.mutation({
      queryFn: ({ id, body }) => run(patchGeneric(`${BRANDS}/${id}`, body)),
      invalidatesTags: ['Brand'],
    }),
    bulkSetSegment: b.mutation({
      queryFn: ({ brandIds, segment }) => run(post(`${BRANDS}/bulk-segment`, { brandIds, segment })),
      invalidatesTags: ['Brand'],
    }),
    createPocMismatch: b.mutation({
      queryFn: ({ id, rawPoc, sourceTabs }) => run(post(`${BRANDS}/${id}/poc-mismatches`, { rawPoc, sourceTabs })),
      invalidatesTags: ['Brand'],
    }),
    listPeople: b.query({
      queryFn: () => run(get(PEOPLE)),
      providesTags: ['Person'],
    }),
    createPerson: b.mutation({
      queryFn: (body) => run(post(PEOPLE, body)),
      invalidatesTags: ['Person'],
    }),
    createDeal: b.mutation({
      queryFn: (body) => run(post(DEALS, body)),
      invalidatesTags: ['Brand'],
    }),
  }),
})

export const {
  useListBrandsQuery,
  useGetBrandQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useArchiveBrandMutation,
  useLinkDealMutation,
  useUnlinkDealMutation,
  useCorrectMappingMutation,
  usePatchBrandMutation,
  useBulkSetSegmentMutation,
  useCreatePocMismatchMutation,
  useListPeopleQuery,
  useCreatePersonMutation,
  useCreateDealMutation,
} = growthosBrandsApiSlice
